import { useCallback, useState } from "react";
import { useCreateApiToken } from "src/queries/auth/tokens.mutation";

export type TokenType = "api" | "agent";

const INITIAL_VALUES = {
  tokenType: "api" as TokenType,
  label: "",
  expiresInDays: 365,
  readOnly: false,
  generatedToken: null as string | null,
};

const TOKEN_TYPE_LABELS: Record<TokenType, string> = {
  api: "API",
  agent: "Agent",
};

export const EXPIRY_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "180 days" },
  { value: 365, label: "1 year" },
  { value: 1825, label: "5 years" },
] as const;

export function useGenerateTokenForm() {
  const [tokenType, setTokenType] = useState<TokenType>(INITIAL_VALUES.tokenType);
  const [label, setLabel] = useState(INITIAL_VALUES.label);
  const [expiresInDays, setExpiresInDays] = useState(INITIAL_VALUES.expiresInDays);
  const [readOnly, setReadOnly] = useState(INITIAL_VALUES.readOnly);
  const [generatedToken, setGeneratedToken] = useState<string | null>(
    INITIAL_VALUES.generatedToken
  );
  const createMutation = useCreateApiToken();

  const reset = useCallback(() => {
    setTokenType(INITIAL_VALUES.tokenType);
    setLabel(INITIAL_VALUES.label);
    setExpiresInDays(INITIAL_VALUES.expiresInDays);
    setReadOnly(INITIAL_VALUES.readOnly);
    setGeneratedToken(INITIAL_VALUES.generatedToken);
  }, []);

  const typeLabel = TOKEN_TYPE_LABELS[tokenType];

  const handleCreate = useCallback(async () => {
    const response = await createMutation.mutateAsync({
      label: label || undefined,
      expires_in_days: expiresInDays,
      token_type: tokenType,
      // read-only applies to API tokens only — agent tokens are write-only.
      read_only: tokenType === "api" ? readOnly : undefined,
    });
    const data = await response.json();
    setGeneratedToken(data.token);
  }, [createMutation, expiresInDays, label, tokenType, readOnly]);

  return {
    tokenType,
    setTokenType,
    label,
    setLabel,
    expiresInDays,
    setExpiresInDays,
    readOnly,
    setReadOnly,
    generatedToken,
    typeLabel,
    createMutation,
    handleCreate,
    reset,
  };
}
